import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import * as XLSX from "xlsx";
import PdfPreview from '../../../../Common/PDFPreview';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { RingLoader } from 'react-spinners';
import { Typography, Button } from '@mui/material';
import PrintButton from "../../../../Common/Buttons/PrintPDF";
import { formatReadableAmount } from "../../../../Common/FormatFunctions/FormatAmount";
import SearchBar from "../../../../Common/UtilityCommon/SearchBar";

const API_URL = process.env.REACT_APP_API;

const SelfStockReport = () => {
  const companyName = sessionStorage.getItem("Company_Name");
  const Company_GSTNO = sessionStorage.getItem('Company_GSTNO');

  const [groupedData, setGroupedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfPreview, setPdfPreview] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/self-stock-report`, {
          params: {
            Company_Code: sessionStorage.getItem("Company_Code"),
            Year_Code: sessionStorage.getItem("Year_Code"),
          },
        });
        groupByBuyerName(response.data);
      } catch (err) {
        setError("Failed to load data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateGrandTotals = (data) => {
    const totals = {
      totalQuintal: 0,
      totalDispatch: 0,
      totalBalance: 0,
    };

    data.forEach(group => {
      group.details.forEach(record => {
        totals.totalQuintal += parseFloat(record.Buyer_Quantal || 0);
        totals.totalDispatch += parseFloat(record.DESPATCH || 0);
        totals.totalBalance += parseFloat(record.BALANCE || 0);
      });
    });

    return totals;
  };

  const groupByBuyerName = (data) => {
    const grouped = data.reduce((acc, curr) => {
      if (parseFloat(curr.BALANCE) !== 0) {
        if (!acc[curr.buyername]) {
          acc[curr.buyername] = { buyername: curr.buyername, details: [], totalBalance: 0 };
        }
        acc[curr.buyername].details.push(curr);
        acc[curr.buyername].totalBalance += parseFloat(curr.BALANCE);
      }
      return acc;
    }, {});
    setGroupedData(Object.values(grouped));
  };

  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedData = [...groupedData];
    sortedData.forEach(group => {
      group.details.sort((a, b) => {
        if (key === "Tender_No" || key === "Grade") {
          if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
          if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
          return 0;
        } else {
          if (parseFloat(a[key] || 0) < parseFloat(b[key] || 0)) return direction === 'asc' ? -1 : 1;
          if (parseFloat(a[key] || 0) > parseFloat(b[key] || 0)) return direction === 'asc' ? 1 : -1;
          return 0;
        }
      });
    });

    setGroupedData(sortedData);
    setSortConfig({ key, direction });
  };

  const filteredData = groupedData
    .map(group => {
      const filteredDetails = group.details.filter(record =>
        Object.values(record).join(' ').toLowerCase().includes(searchQuery.toLowerCase())
      );
      return { ...group, details: filteredDetails };
    })
    .filter(group => group.details.length > 0);

  const handleExportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const sheetData = [["Buyer Name", "Tender No", "Mill Name", "Grade", "MR", "SR", "Qntl", "Desp", "Bal", "Lift", "DO"]];

    filteredData.forEach((group) => {
      sheetData.push([group.buyername, "", "", "", "", "", "", "", group.totalBalance]);
      group.details.forEach((record) => {
        sheetData.push(["", record.Tender_No, record.millshortname, record.Grade, record.Mill_Rate, record.Sale_Rate, record.Buyer_Quantal, record.DESPATCH, record.BALANCE, record.Lifting_Date, record.tenderdoshortname]);
      });
    });

    const sheet = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, sheet, "Self Stock Report");
    XLSX.writeFile(workbook, "SelfStockReport.xlsx");
  };

  const generatePDF = () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    let yOffset = 10;

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 255);
    doc.setFont("helvetica", "bold");
    const companyNameWidth = doc.getTextWidth(companyName);
    doc.text(companyName, (pageWidth - companyNameWidth) / 2, yOffset);

    yOffset += 7;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    const title = "Self Stock Report";
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, yOffset);

    yOffset += 8;

    filteredData.forEach((group) => {
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(`Buyer: ${group.buyername} (Total Balance: ${group.totalBalance.toFixed(2)})`, 10, yOffset);
      yOffset += 5;

      const tableBody = group.details.map((record) => [
        record.Tender_No,
        record.millshortname,
        record.Grade,
        formatReadableAmount(record.Mill_Rate),
        formatReadableAmount(record.Sale_Rate),
        formatReadableAmount(record.Buyer_Quantal),
        formatReadableAmount(record.DESPATCH),
        formatReadableAmount(record.BALANCE),
        record.Tender_Date,
        record.tenderdoshortname,
      ]);
      doc.autoTable({
        startY: yOffset,
        head: [["Tender No", "Mill Name", "Grade", "Mill Rate", "Sale Rate", "Quintal", "Desp", "Balance", "Liftting", "DO"]],
        body: tableBody,
        theme: "grid",
        headStyles: { fillColor: [0, 0, 120], textColor: [255, 255, 255] },
        margin: { left: 10, right: 10 },
        columnStyles: {
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' },
          7: { halign: 'right' },
        },
        didDrawPage: (data) => {
          yOffset = data.cursor.y + 10;
        },
      });
    });

    const totals = calculateGrandTotals(filteredData);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Grand Totals : Quintal = ${formatReadableAmount(totals.totalQuintal.toFixed(2))}, Dispatched = ${formatReadableAmount(totals.totalDispatch.toFixed(2))}, Balance = ${formatReadableAmount(totals.totalBalance.toFixed(2))}`,
      10,
      yOffset
    );

    const pdfBlob = doc.output("blob");
    setPdfPreview(URL.createObjectURL(pdfBlob));
  };

  return (
    <>
      <div style={{ position: 'relative', marginTop: "-90px" }}>
        <Typography variant="h6" style={{ textAlign: 'center', fontSize: "18px", fontWeight: "bold", color: "blue" }}>Self Stock Report</Typography>

        <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: "10px" }}>
          <div style={{ flex: 1, minWidth: "250px", maxWidth: "1100px" }}>
            <SearchBar value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
            {pdfPreview && <PdfPreview pdfData={pdfPreview} apiData={filteredData} label={"Self Stock Report"} />}
            <PrintButton disabledFeild={""} fetchData={generatePDF} />
            <Button variant="outlined" color="secondary" onClick={handleExportToExcel}>
              Export to Excel
            </Button>
          </div>
        </div>

        {error && <p className="text-danger">{error}</p>}

        <div id="reportContent" style={{
          maxHeight: 'calc(100vh - 200px)',
          overflow: 'auto',
          border: '1px solid #e0e0e0',
          position: 'relative'
        }}>
          <table className="table" style={{
            borderCollapse: "collapse",
            width: "100%",
            marginBottom: "50px",
            position: 'relative'
          }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ backgroundColor: "black", color: "white", textAlign: "center" }}>
                {[
                  { label: "Tender No", key: "Tender_No" },
                  { label: "Mill Name", key: "millshortname" },
                  { label: "Grade", key: "Grade" },
                  { label: "Mill Rate", key: "Mill_Rate" },
                  { label: "Sale Rate", key: "Sale_Rate" },
                  { label: "Quintal", key: "Buyer_Quantal" },
                  { label: "Desp", key: "DESPATCH" },
                  { label: "Balance", key: "BALANCE" },
                ].map(col => (
                  <th key={col.key} onClick={() => sortData(col.key)} style={{ border: "1px solid white", padding: '8px', cursor: 'pointer' }}>
                    {col.label} {sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                  </th>
                ))}
                <th style={{ border: "1px solid white", padding: '8px' }}>Liftting Date</th>
                <th style={{ border: "1px solid white", padding: '8px' }}>DO</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                    <td colSpan="10" style={{ textAlign: "left", padding: '8px' }}>
                      {group.buyername} (Total Balance: {formatReadableAmount(group.totalBalance.toFixed(2))})
                    </td>
                  </tr>
                  {group.details.map((record, recordIndex) => (
                    <tr key={recordIndex} style={{ textAlign: "center" }}>
                      <td style={{ border: "1px dashed black", padding: '8px' }}>{record.Tender_No}</td>
                      <td align="left" style={{ border: "1px dashed black", padding: '8px' }}>{record.millshortname}</td>
                      <td align="left" style={{ border: "1px dashed black", padding: '8px' }}>{record.Grade}</td>
                      <td align="right" style={{ border: "1px dashed black", padding: '8px' }}>{formatReadableAmount(record.Mill_Rate)}</td>
                      <td align="right" style={{ border: "1px dashed black", padding: '8px' }}>{formatReadableAmount(record.Sale_Rate)}</td>
                      <td align="right" style={{ border: "1px dashed black", padding: '8px' }}>{formatReadableAmount(record.Buyer_Quantal)}</td>
                      <td align="right" style={{ border: "1px dashed black", padding: '8px' }}>{record.DESPATCH}</td>
                      <td align="right" style={{ border: "1px dashed black", padding: '8px' }}>{formatReadableAmount(record.BALANCE)}</td>
                      <td style={{ border: "1px dashed black", padding: '8px' }}>{record.Tender_Date}</td>
                      <td align="left" style={{ border: "1px dashed black", padding: '8px' }}>{record.tenderdoshortname}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                <td colSpan="5" style={{ textAlign: "center", padding: '8px' }}>Grand Totals</td>
                <td align="right" style={{ border: "1px dashed black", padding: '8px' }}>{formatReadableAmount(calculateGrandTotals(filteredData).totalQuintal.toFixed(2))}</td>
                <td align="right" style={{ border: "1px dashed black", padding: '8px' }}>{formatReadableAmount(calculateGrandTotals(filteredData).totalDispatch.toFixed(2))}</td>
                <td align="right" style={{ border: "1px dashed black", padding: '8px' }}>{formatReadableAmount(calculateGrandTotals(filteredData).totalBalance.toFixed(2))}</td>
                <td colSpan="2" style={{ border: "1px dashed black", padding: '8px' }}></td>
              </tr>
            </tbody>
          </table>
          {loading && (
            <div style={{
              display: "flex", justifyContent: "center", alignItems: "center", height: "100%",
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 3
            }}>
              <RingLoader />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SelfStockReport;